import { createId } from "@paralleldrive/cuid2";
import {
  AgentWorkflowDefinition,
  Edge as WorkflowEdge,
  TriggerNode as TriggerNodeType,
  ActionNode as ActionNodeType,
  AgentNode as AgentNodeType,
  FlowEditorNode,
  FlowEditorEdge,
} from "@/store/agentsStore";
import { MarkerType } from "@xyflow/react";

const defaultEdgeOptions = {
  type: "DEFAULT" as const,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
};

/**
 * Converts workflow definition nodes to React Flow nodes
 */
export function workflowNodesToReactFlow(
  workflowDefinition: AgentWorkflowDefinition | null,
  workflowVersionId?: string,
): FlowEditorNode[] {
  if (!workflowDefinition) {
    return [];
  }

  return workflowDefinition.nodes.map((node): FlowEditorNode => {
    const type = node.type;

    if (type === "TRIGGER") {
      return {
        id: node.id,
        type: "TRIGGER",
        position: node.position,
        data: {
          node: node,
          workflowVersionId,
        },
      };
    }

    if (type === "AGENT") {
      return {
        id: node.id,
        type: "AGENT",
        position: node.position,
        data: {
          node: node,
          workflowVersionId,
        },
      };
    }

    return {
      id: node.id,
      type,
      position: node.position,
      data: {
        node,
        workflowVersionId,
      },
    };
  });
}

/**
 * Converts workflow definition edges to React Flow edges
 */
export function workflowEdgesToReactFlow(
  workflowDefinition: AgentWorkflowDefinition | null,
): FlowEditorEdge[] {
  if (!workflowDefinition) {
    return [];
  }

  return workflowDefinition.edges.map((edge) => ({
    id: edge.id,
    source: edge.from,
    target: edge.to,
    data: { edge },
    ...defaultEdgeOptions,
  }));
}

/**
 * Creates a new trigger node
 */
export function createTriggerNode(
  triggerId: string,
  position = { x: 250, y: 50 },
): TriggerNodeType {
  return {
    id: createId(),
    type: "TRIGGER",
    triggerId,
    triggerConfiguration: [],
    position,
  };
}

/**
 * Creates a new action node
 */
export function createActionNode(
  actionId: string,
  actionType: ActionNodeType["type"] | AgentNodeType["type"],
  position = { x: 250, y: 200 },
): ActionNodeType | AgentNodeType {
  if (actionType === "AGENT") {
    return {
      id: createId(),
      type: actionType,
      actionId,
      actionConfiguration: [],
      position,
      actions: [],
    };
  }

  return {
    id: createId(),
    type: actionType,
    actionId,
    actionConfiguration: [],
    position,
  };
}

/**
 * Creates a new edge between nodes
 */
export function createEdge(from: string, to: string): WorkflowEdge {
  return {
    id: createId(),
    from,
    to,
    condition: "",
  };
}

/**
 * Syncs React Flow node positions back to workflow definition nodes
 */
export function syncNodePositions(
  workflowNodes: (TriggerNodeType | ActionNodeType | AgentNodeType)[],
  reactFlowNodes: FlowEditorNode[],
): (TriggerNodeType | ActionNodeType | AgentNodeType)[] {
  return workflowNodes.map((node) => {
    const rfNode = reactFlowNodes.find((n) => n.id === node.id);
    if (rfNode && rfNode.position) {
      return { ...node, position: rfNode.position };
    }
    return node;
  });
}
